import React, { useState, useEffect } from 'react';
import { api } from '../services/api';

const AISettings = () => {
  const [settings, setSettings] = useState({
    googleApiKey: '',
    grokApiKey: '',
    grokApiKey2: '',
    deepseekApiKey: '',
    deepseekApiKey2: '',
    selectedProvider: 'grok', // 'grok' | 'deepseek' | 'google'
    modelSettings: {
      google: {
        model: 'gemini-2.5-flash',
        temperature: 0.7,
        maxTokens: 1000
      },
      grok: {
        model: 'grok-4.3',
        temperature: 0.8,
        maxTokens: 1000
      },
      deepseek: {
        model: 'deepseek-chat',
        temperature: 0.8,
        maxTokens: 1000
      }
    }
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  // DeepSeek cache-hit stats — refreshed every 30s so the badge stays
  // close to real-time without thrashing the server.
  const [cacheStats, setCacheStats] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await api.get('/ai/cache-stats?windowMinutes=60');
        if (!cancelled) setCacheStats(res.data);
      } catch (_) { /* non-fatal */ }
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/ai/settings');
      if (response.data) {
        setSettings(prev => ({ 
          ...prev, 
          ...response.data,
          selectedProvider: response.data.defaultModel || 'google'
        }));
      }
    } catch (error) {
      console.error('Failed to load AI settings:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage('');
    
    try {
      await api.post('/ai/settings', {
        googleApiKey: settings.googleApiKey,
        grokApiKey: settings.grokApiKey,
        grokApiKey2: settings.grokApiKey2,
        deepseekApiKey: settings.deepseekApiKey,
        deepseekApiKey2: settings.deepseekApiKey2,
        defaultModel: settings.selectedProvider,
        modelSettings: settings.modelSettings
      });
      setMessage('AI settings saved successfully!');
    } catch (error) {
      setMessage('Failed to save AI settings: ' + error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async (provider) => {
    setLoading(true);
    setMessage('');
    
    try {
      // Pass the operator's currently-typed key + chosen model so they
      // can test BEFORE clicking Save. Masked values (starting with
      // '***' — the loaded indicator) are forwarded as-is; the backend
      // treats them as "untouched" and falls back to the saved value.
      const keyForProvider = {
        grok: settings.grokApiKey,
        deepseek: settings.deepseekApiKey,
        google: settings.googleApiKey
      }[provider];
      const modelForProvider = settings.modelSettings?.[provider]?.model;
      const response = await api.post('/ai/test-connection', {
        provider,
        apiKey: keyForProvider,
        model: modelForProvider
      });
      if (response.data.success) {
        setMessage(`✅ ${response.data.message || `${provider} connection test successful`}`);
      } else {
        setMessage(`❌ ${response.data.message || `${provider} connection test failed`}`);
      }
    } catch (error) {
      setMessage(`❌ ${error.response?.data?.message || error.response?.data?.error || error.message || `${provider} connection test failed`}`);
    } finally {
      setLoading(false);
    }
  };

  const updateModelSettings = (provider, field, value) => {
    setSettings(prev => ({
      ...prev,
      modelSettings: {
        ...prev.modelSettings,
        [provider]: {
          ...prev.modelSettings[provider],
          [field]: value
        }
      }
    }));
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">AI Settings</h1>
        
        {message && (
          <div className={`mb-4 p-3 rounded ${
            message.includes('Success') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {message}
          </div>
        )}

        {/* DeepSeek cache-hit badge — only shown if at least one
            DeepSeek request has been made since the server started so
            we have something to report. */}
        {cacheStats?.configured && cacheStats?.active && cacheStats?.requestCount > 0 && (
          <div className="mb-6 flex items-center justify-between rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 px-4 py-3">
            <div className="text-sm">
              <span className="font-semibold text-indigo-900 dark:text-indigo-200">DeepSeek input-token cache</span>
              <span className="ml-2 text-indigo-700 dark:text-indigo-300">
                {(cacheStats.hitRate * 100).toFixed(1)}% hit · {cacheStats.hitTokens.toLocaleString()} hit / {cacheStats.missTokens.toLocaleString()} miss
              </span>
            </div>
            <div className="text-xs text-indigo-600 dark:text-indigo-400">
              last hour · {cacheStats.requestCount.toLocaleString()} request{cacheStats.requestCount === 1 ? '' : 's'}
            </div>
          </div>
        )}

        {/* Provider Selection */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Select AI Provider</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            
            {/* Google Gemini Option */}
            <div className={`border-2 rounded-2xl p-4 cursor-pointer transition-all ${
              settings.selectedProvider === 'google' 
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
            }`} onClick={() => setSettings(prev => ({ ...prev, selectedProvider: 'google' }))}>
              <div className="flex items-center mb-3">
                <input
                  type="radio"
                  name="provider"
                  value="google"
                  checked={settings.selectedProvider === 'google'}
                  onChange={() => setSettings(prev => ({ ...prev, selectedProvider: 'google' }))}
                  className="mr-3"
                />
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">G</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 dark:text-gray-100">Google Gemini</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Advanced AI with reasoning capabilities</p>
                  </div>
                </div>
              </div>
              
              {/* Google API Key */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  API Key
                  {settings.googleApiKey && settings.googleApiKey.startsWith('***') && (
                    <span className="ml-2 text-xs text-green-600 dark:text-green-400 font-medium">✓ Configured</span>
                  )}
                </label>
                <input
                  type="password"
                  value={settings.googleApiKey}
                  onChange={(e) => setSettings(prev => ({ ...prev, googleApiKey: e.target.value }))}
                  placeholder="AIza..."
                  className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                    settings.googleApiKey && settings.googleApiKey.startsWith('***') 
                      ? 'border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-600' 
                      : 'border-gray-300'
                  }`}
                />
              </div>

              {/* Google Model Selection */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Model</label>
                <select
                  value={settings.modelSettings.google.model}
                  onChange={(e) => updateModelSettings('google', 'model', e.target.value)}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="gemini-2.5-flash">Gemini 2.5 Flash (Recommended)</option>
                  <option value="gemini-2.5-pro">Gemini 2.5 Pro</option>
                  <option value="gemini-2.0-flash">Gemini 2.0 Flash</option>
                </select>
              </div>

              {/* Test Connection Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleTestConnection('google');
                }}
                disabled={loading || !settings.googleApiKey}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50"
              >
                Test Connection
              </button>
            </div>

            {/* Grok Option */}
            <div className={`border-2 rounded-2xl p-4 cursor-pointer transition-all ${
              settings.selectedProvider === 'grok' 
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
            }`} onClick={() => setSettings(prev => ({ ...prev, selectedProvider: 'grok' }))}>
              <div className="flex items-center mb-3">
                <input
                  type="radio"
                  name="provider"
                  value="grok"
                  checked={settings.selectedProvider === 'grok'}
                  onChange={() => setSettings(prev => ({ ...prev, selectedProvider: 'grok' }))}
                  className="mr-3"
                />
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">X</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 dark:text-gray-100">Grok (X.AI)</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Latest AI with fast reasoning</p>
                  </div>
                </div>
              </div>
              
              {/* Grok API Key */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  API Key
                  {settings.grokApiKey && settings.grokApiKey.startsWith('***') && (
                    <span className="ml-2 text-xs text-green-600 dark:text-green-400 font-medium">✓ Configured</span>
                  )}
                </label>
                <input
                  type="password"
                  value={settings.grokApiKey}
                  onChange={(e) => setSettings(prev => ({ ...prev, grokApiKey: e.target.value }))}
                  placeholder="xai-..."
                  className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                    settings.grokApiKey && settings.grokApiKey.startsWith('***') 
                      ? 'border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-600' 
                      : 'border-gray-300'
                  }`}
                />
              </div>

              {/* Grok API Key 2 (optional - requests split between keys) */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  API Key 2 (optional)
                  {settings.grokApiKey2 && settings.grokApiKey2.startsWith('***') && (
                    <span className="ml-2 text-xs text-green-600 dark:text-green-400 font-medium">✓ Configured</span>
                  )}
                </label>
                <input
                  type="password"
                  value={settings.grokApiKey2 || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, grokApiKey2: e.target.value }))}
                  placeholder="xai-... (splits traffic with Key 1)"
                  className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                    settings.grokApiKey2 && settings.grokApiKey2.startsWith('***') 
                      ? 'border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-600' 
                      : 'border-gray-300'
                  }`}
                />
              </div>

              {/* Grok Model Selection */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Model</label>
                <select
                  value={settings.modelSettings.grok.model}
                  onChange={(e) => updateModelSettings('grok', 'model', e.target.value)}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="grok-4.3">Grok 4.3 (Latest)</option>
                  <option value="grok-4-fast-reasoning">Grok-4 Fast Reasoning</option>
                  <option value="grok-4-fast-non-reasoning">Grok-4 Fast Non-Reasoning</option>
                  <option value="grok-3-mini">Grok-3 Mini</option>
                </select>
              </div>

              {/* Test Connection Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleTestConnection('grok');
                }}
                disabled={loading || !settings.grokApiKey}
                className="w-full px-4 py-2 bg-orange-600 text-white rounded-xl hover:bg-orange-700 disabled:opacity-50"
              >
                Test Connection
              </button>
            </div>

            {/* DeepSeek Option */}
            <div className={`border-2 rounded-2xl p-4 cursor-pointer transition-all ${
              settings.selectedProvider === 'deepseek'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
            }`} onClick={() => setSettings(prev => ({ ...prev, selectedProvider: 'deepseek' }))}>
              <div className="flex items-center mb-3">
                <input
                  type="radio"
                  name="provider"
                  value="deepseek"
                  checked={settings.selectedProvider === 'deepseek'}
                  onChange={() => setSettings(prev => ({ ...prev, selectedProvider: 'deepseek' }))}
                  className="mr-3"
                />
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">DS</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 dark:text-gray-100">DeepSeek</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Input-token cache · ~50× cheaper on chat</p>
                  </div>
                </div>
              </div>

              {/* DeepSeek API Key */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  API Key
                  {settings.deepseekApiKey && settings.deepseekApiKey.startsWith('***') && (
                    <span className="ml-2 text-xs text-green-600 dark:text-green-400 font-medium">✓ Configured</span>
                  )}
                </label>
                <input
                  type="password"
                  value={settings.deepseekApiKey || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, deepseekApiKey: e.target.value }))}
                  placeholder="sk-..."
                  className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                    settings.deepseekApiKey && settings.deepseekApiKey.startsWith('***')
                      ? 'border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-600'
                      : 'border-gray-300'
                  }`}
                />
              </div>

              {/* DeepSeek API Key 2 (optional - requests split between keys) */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  API Key 2 (optional)
                  {settings.deepseekApiKey2 && settings.deepseekApiKey2.startsWith('***') && (
                    <span className="ml-2 text-xs text-green-600 dark:text-green-400 font-medium">✓ Configured</span>
                  )}
                </label>
                <input
                  type="password"
                  value={settings.deepseekApiKey2 || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, deepseekApiKey2: e.target.value }))}
                  placeholder="sk-... (splits traffic with Key 1)"
                  className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                    settings.deepseekApiKey2 && settings.deepseekApiKey2.startsWith('***')
                      ? 'border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-600'
                      : 'border-gray-300'
                  }`}
                />
              </div>

              {/* DeepSeek Model Selection */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Model</label>
                <select
                  value={settings.modelSettings.deepseek.model}
                  onChange={(e) => updateModelSettings('deepseek', 'model', e.target.value)}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="deepseek-chat">deepseek-chat (v4-flash · cheapest · recommended)</option>
                  <option value="deepseek-reasoner">deepseek-reasoner (v4-flash thinking mode)</option>
                  <option value="deepseek-v4-flash">deepseek-v4-flash (explicit)</option>
                  <option value="deepseek-v4-pro">deepseek-v4-pro (smarter, ~3× cost)</option>
                </select>
              </div>

              {/* Vision note — DeepSeek text models don't accept images;
                  UniversalChatSessionManager runs a one-shot Grok
                  describer first and feeds the description text into
                  DeepSeek so the conversation flow still "sees" the
                  photo. */}
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-3">
                ℹ️ Images: a one-shot Grok call describes the photo, then DeepSeek replies based on the description. Keep a Grok key configured for this to work.
              </p>

              {/* Test Connection Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleTestConnection('deepseek');
                }}
                disabled={loading || !settings.deepseekApiKey}
                className="w-full px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50"
              >
                Test Connection
              </button>
            </div>
          </div>
        </div>

        {/* Advanced Settings — only meaningful for providers that have
            tunable temperature / max-tokens (i.e. not 'template'). The
            null-guard prevents "cannot read properties of undefined"
            when a legacy setting (e.g. 'google') is selected. */}
        {settings.modelSettings?.[settings.selectedProvider] && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Advanced Settings <span className="text-sm font-normal text-gray-500">({settings.selectedProvider})</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Temperature
              </label>
              <input
                type="range"
                min="0"
                max="2"
                step="0.1"
                value={settings.modelSettings[settings.selectedProvider].temperature}
                onChange={(e) => updateModelSettings(settings.selectedProvider, 'temperature', parseFloat(e.target.value))}
                className="w-full"
              />
              <span className="text-sm text-gray-600 dark:text-gray-300">{settings.modelSettings[settings.selectedProvider].temperature}</span>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Max Tokens
              </label>
              <input
                type="number"
                min="100"
                max="4000"
                value={settings.modelSettings[settings.selectedProvider].maxTokens}
                onChange={(e) => updateModelSettings(settings.selectedProvider, 'maxTokens', parseInt(e.target.value))}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
        </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AISettings;
